import React, { useMemo } from "react";

import {
  Chip,
  Button,
  Switch,
  Checkbox,
  TextField,
  FormControl,
  ListItemText,
  Autocomplete,
  FormControlLabel,
  createFilterOptions,
} from "@mui/material";
import { checkIsCurrentUserOfClinic } from "utils/currentUser.util";

const ReportNoteTemplateInputForm = (props) => {
  const { formikProps, selectorsData, onCloseModal } = props;

  const {
    values,
    errors,
    touched,

    resetForm,
    isSubmitting,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
  } = formikProps;

  const filter = createFilterOptions();

  const handleClose = () => {
    onCloseModal();
    resetForm({ values: {} });
  };

  const isClinicUser = checkIsCurrentUserOfClinic();

  const noteSectionOptions = useMemo(() => {
    const sections = selectorsData?.sections || [];

    if (!values?.account) {
      return sections.filter((section) => !section?.account);
    }

    return [
      ...sections.filter((section) => section?.account === values?.account),
      ...sections.filter((section) => !section?.account),
    ];
  }, [selectorsData?.sections, values?.account]);

  const isInOptionList = useMemo(() => {
    const sections = noteSectionOptions?.map((option) => option?._id);

    return values.sections?.every((section) => sections?.includes(section._id));
  }, [values.sections, noteSectionOptions]);

  return (
    <form style={{ minWidth: "500px" }}>
      <div style={{ marginTop: "2%", marginBottom: "2%" }}>
        <FormControl style={{ width: "100%" }}>
          <Autocomplete
            id="device_group_name"
            name="device_group_name"
            value={values?.device_group_name}
            options={selectorsData?.deviceGroups?.map((item) => item.name)}
            onChange={(e, value) => {
              setFieldTouched("device_group_name");
              setFieldValue("device_group_name", value);
            }}
            style={{ width: "100%", marginBottom: "2%" }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Device Group"
                variant="outlined"
                type="text"
                error={errors.device_group_name}
                helperText={errors?.device_group_name}
              />
            )}
          />
        </FormControl>
      </div>
      <div style={{ margin: "2% 0" }}>
        <FormControl style={{ width: "100%" }}>
          <Autocomplete
            id="billingCode"
            name="billingCode"
            value={values.billingCode || null}
            options={selectorsData?.billingCodes?.map((item) =>
              item?.toString()?.trim()
            )}
            onChange={(e, value) => {
              setFieldTouched("billingCode");
              setFieldValue("billingCode", value || undefined);
            }}
            style={{ width: "100%", marginBottom: "2%" }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Billing Code"
                variant="outlined"
                type="text"
                error={errors.billingCode}
                helperText={errors?.billingCode}
              />
            )}
          />
        </FormControl>
      </div>
      <div style={{ margin: "2% 0" }}>
        <FormControl style={{ width: "100%" }}>
          <Autocomplete
            id="account"
            name="account"
            value={values.account || null}
            options={selectorsData?.accounts}
            onChange={(e, value) => {
              setFieldTouched("account");
              setFieldValue("account", value || undefined);
            }}
            style={{ width: "100%", marginBottom: "2%" }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Account"
                variant="outlined"
                type="text"
                required={isClinicUser}
                error={touched.account && !!errors.account}
                helperText={touched.account && errors.account}
              />
            )}
          />
        </FormControl>
      </div>
      <div style={{ margin: "2% 0" }}>
        <FormControl
          fullWidth
          variant="outlined"
          style={{ width: "100%" }}
          error={(touched.sections && errors.sections) || !isInOptionList}
        >
          <Autocomplete
            multiple
            disableCloseOnSelect
            id="sections"
            name="sections"
            value={values.sections || []}
            options={noteSectionOptions || []}
            groupBy={(option) => option?.account || "Default"}
            renderGroup={(params) => (
              <li key={params.key}>
                <div
                  style={{ backgroundColor: "#d0d0d0", padding: "5px 10px" }}
                >
                  <h3 style={{ fontSize: "16px" }}>{params.group}</h3>
                </div>
                <ul style={{ paddingLeft: 0 }}>{params.children}</ul>
              </li>
            )}
            filterOptions={(options, params) => {
              const filtered = filter(options, params);
              return filtered.length ? filtered : options;
            }}
            getOptionLabel={(option) => option.note}
            noOptionsText="No options"
            isOptionEqualToValue={(option, value) => option._id === value._id}
            renderOption={(props, option, { selected }) => {
              const { key, ...optionProps } = props;

              const sectionIndex = values.sections?.findIndex(
                ({ _id }) => _id === option?._id
              );

              return (
                <li key={key} {...optionProps}>
                  <Checkbox style={{ marginRight: 8 }} checked={selected} />
                  <ListItemText
                    primary={option.note}
                    secondary={option?.placeholders?.map(
                      (placeholder, index) => (
                        <Chip
                          key={index}
                          label={placeholder}
                          style={{
                            margin: "5px",
                            height: "25px",
                            backgroundColor: selected ? "#e0dcdc" : "#ececec",
                          }}
                        />
                      )
                    )}
                    primaryTypographyProps={{
                      style: { whiteSpace: "normal" },
                    }}
                    secondaryTypographyProps={{
                      style: { whiteSpace: "normal" },
                    }}
                  />
                  {selected && (
                    <Chip
                      label={sectionIndex + 1}
                      style={{
                        margin: "1%",
                        color: "#fff",
                        backgroundColor: "#94BA33",
                      }}
                    />
                  )}
                </li>
              );
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });

                const isSectionInOptionList = noteSectionOptions.some(
                  (section) => section?._id === option?._id
                );

                return (
                  <Chip
                    key={key}
                    label={option.note}
                    color={isSectionInOptionList ? "default" : "error"}
                    variant={isSectionInOptionList ? "filled" : "outlined"}
                    style={{ margin: "1%", backgroundColor: "#ececec" }}
                    {...tagProps}
                  />
                );
              })
            }
            onBlur={() => setFieldTouched("sections")}
            onChange={(e, value) => {
              setFieldTouched("sections");
              setFieldValue("sections", value);
            }}
            style={{ width: "100%", marginBottom: "2%" }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Sections"
                variant="outlined"
                required
                error={(touched.sections && errors.sections) || !isInOptionList}
                helperText={
                  (touched.sections && errors.sections) ||
                  (!isInOptionList &&
                    "Some selected sections are not in the options.")
                }
              />
            )}
          />
        </FormControl>
      </div>
      <div
        style={{
          display: "inline-flex",
          justifyContent: "center",
          float: "right",
          gap: "10px",
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={values?.status}
              onChange={(e) => setFieldValue("status", e.target.checked)}
              name="status"
              id="status"
              value={values?.status}
              inputProps={{ "aria-label": "secondary checkbox" }}
            />
          }
          label="Active"
          labelPlacement="start"
        />
        <Button
          variant="outlined"
          color="secondary"
          disabled={isSubmitting}
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          type="button"
          color="secondary"
          variant="contained"
          onClick={!isInOptionList || isSubmitting ? null : handleSubmit}
          disabled={!isInOptionList || isSubmitting}
          style={{ margin: "1%", marginLeft: "5%" }}
        >
          {isSubmitting ? "Saving..." : "Save"}
        </Button>
      </div>
    </form>
  );
};

export default ReportNoteTemplateInputForm;
